import React from 'react';
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Button,
  Divider,
  Avatar,
  Grid,
  Card,
  CardContent,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import TwitterIcon from '@mui/icons-material/Twitter';
import LinkIcon from '@mui/icons-material/Link';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Helmet } from 'react-helmet-async';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { blogData } from '../data/blogData';

const SITE_URL = 'https://www.slgt.in';

// Renders blog.content (Markdown) with site-matched styling, routes internal
// links through React Router instead of a full page reload, and turns CTA
// blockquotes into a branded callout box.
const markdownComponents = {
  h2: ({ children }) => (
    <Typography variant="h4" component="h2" sx={{ fontFamily: 'Nunito, serif', fontWeight: 700, mt: 5, mb: 2 }}>
      {children}
    </Typography>
  ),
  h3: ({ children }) => (
    <Typography variant="h6" component="h3" sx={{ fontFamily: 'Nunito, serif', fontWeight: 700, mt: 3, mb: 1.5 }}>
      {children}
    </Typography>
  ),
  p: ({ children }) => (
    <Typography variant="body1" sx={{ fontFamily: 'Nunito, serif', lineHeight: 1.8, mb: 2.5 }}>
      {children}
    </Typography>
  ),
  ul: ({ children }) => (
    <Box component="ul" sx={{ fontFamily: 'Nunito, serif', lineHeight: 1.8, mb: 2.5, pl: 3 }}>
      {children}
    </Box>
  ),
  ol: ({ children }) => (
    <Box component="ol" sx={{ fontFamily: 'Nunito, serif', lineHeight: 1.8, mb: 2.5, pl: 3 }}>
      {children}
    </Box>
  ),
  li: ({ children }) => (
    <Box component="li" sx={{ mb: 0.5 }}>
      {children}
    </Box>
  ),
  a: ({ href, children }) =>
    href && href.startsWith('/') ? (
      <RouterLink to={href} style={{ color: '#FD8112', fontWeight: 600 }}>
        {children}
      </RouterLink>
    ) : (
      <a href={href} target="_blank" rel="noopener noreferrer" style={{ color: '#FD8112', fontWeight: 600 }}>
        {children}
      </a>
    ),
  blockquote: ({ children }) => (
    <Box
      sx={{
        my: 4,
        p: 3,
        borderRadius: 3,
        background: 'linear-gradient(135deg, rgba(253,129,18,0.08), rgba(255,95,109,0.08))',
        border: '1px solid rgba(253,129,18,0.25)',
        '& p': { mb: 0, fontWeight: 600, color: '#14141F' },
      }}
    >
      {children}
    </Box>
  ),
  table: ({ children }) => (
    <TableContainer sx={{ my: 3, border: '1px solid #eee', borderRadius: 2 }}>
      <Table size="small">{children}</Table>
    </TableContainer>
  ),
  thead: ({ children }) => <TableHead sx={{ backgroundColor: '#FD8112' }}>{children}</TableHead>,
  tbody: ({ children }) => <TableBody>{children}</TableBody>,
  tr: ({ children }) => <TableRow>{children}</TableRow>,
  th: ({ children }) => (
    <TableCell sx={{ color: '#fff', fontWeight: 700, fontFamily: 'Nunito, serif' }}>{children}</TableCell>
  ),
  td: ({ children }) => <TableCell sx={{ fontFamily: 'Nunito, serif' }}>{children}</TableCell>,
};

export default function BlogPost() {
  const { slug } = useParams();
  const navigate = useNavigate();

  const blog = blogData.find((post) => post.slug === slug);

  if (!blog) {
    return (
      <Box sx={{ py: 12, textAlign: 'center' }}>
        <Typography variant="h4">Blog post not found</Typography>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{ mt: 2 }}
        >
          Back to Blog
        </Button>
      </Box>
    );
  }

  // Related posts (same category, excluding current post)
  const relatedPosts = blogData
    .filter((post) => post.category === blog.category && post.id !== blog.id)
    .slice(0, 3);

  const postUrl = `${SITE_URL}/blog/${blog.slug}`;
  const isoDate = new Date(blog.date).toISOString();
  const shareText = encodeURIComponent(blog.title);
  const shareUrl = encodeURIComponent(postUrl);
  const metaTitle = blog.metaTitle || blog.title;
  const metaDescription = blog.metaDescription || blog.shortDescription;

  const handleCopyLink = () => {
    navigator.clipboard?.writeText(postUrl);
  };

  return (
    <Box sx={{ py: 8, backgroundColor: '#f8f9fa' }}>
      <Helmet>
        <title>{`${metaTitle} | SLGT Blog`}</title>
        <meta name="description" content={metaDescription} />
        <meta name="keywords" content={blog.tags.join(', ')} />
        <link rel="canonical" href={postUrl} />

        <meta property="og:type" content="article" />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />
        <meta property="og:image" content={blog.image} />
        <meta property="og:url" content={postUrl} />
        <meta property="article:published_time" content={isoDate} />
        <meta property="article:author" content={blog.author} />
        {blog.tags.map((tag) => (
          <meta property="article:tag" content={tag} key={tag} />
        ))}

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={metaTitle} />
        <meta name="twitter:description" content={metaDescription} />
        <meta name="twitter:image" content={blog.image} />

        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BlogPosting",
            "headline": blog.title,
            "description": blog.shortDescription,
            "image": blog.image,
            "datePublished": isoDate,
            "dateModified": isoDate,
            "author": { "@type": "Person", "name": blog.author },
            "publisher": {
              "@type": "Organization",
              "name": "SLGT Digital Marketing Agency",
              "logo": { "@type": "ImageObject", "url": `${SITE_URL}/assets/slgt-logo.png` },
            },
            "mainEntityOfPage": { "@type": "WebPage", "@id": postUrl },
            "keywords": blog.tags.join(', '),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "BreadcrumbList",
            "itemListElement": [
              { "@type": "ListItem", "position": 1, "name": "Home", "item": SITE_URL },
              { "@type": "ListItem", "position": 2, "name": "Blog", "item": `${SITE_URL}/blog` },
              { "@type": "ListItem", "position": 3, "name": blog.title, "item": postUrl },
            ],
          })}
        </script>
        {blog.faqs && blog.faqs.length > 0 && (
          <script type="application/ld+json">
            {JSON.stringify({
              "@context": "https://schema.org",
              "@type": "FAQPage",
              "mainEntity": blog.faqs.map((faq) => ({
                "@type": "Question",
                "name": faq.question,
                "acceptedAnswer": { "@type": "Answer", "text": faq.answer },
              })),
            })}
          </script>
        )}
      </Helmet>

      <Container maxWidth="lg">
        {/* Back Button */}
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/blog')}
          sx={{
            mb: 4,
            color: '#666',
            '&:hover': {
              color: '#FD8112',
            },
          }}
        >
          Back to Blog
        </Button>

        {/* Hero Section */}
        <Box
          sx={{
            position: 'relative',
            height: { xs: '300px', md: '400px' },
            mb: 6,
            borderRadius: 2,
            overflow: 'hidden',
          }}
        >
          <Box
            component="img"
            src={blog.image}
            alt={blog.title}
            sx={{
              width: '100%',
              height: '100%',
              objectFit: 'cover',
            }}
          />
          <Box
            sx={{
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              background: 'linear-gradient(to top, rgba(0,0,0,0.7) 0%, rgba(0,0,0,0) 100%)',
              p: 4,
            }}
          >
            <Typography
              variant="h3"
              component="h1"
              sx={{
                color: 'white',
                fontFamily: 'Nunito, serif',
                fontWeight: '700',
                textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
              }}
            >
              {blog.title}
            </Typography>
          </Box>
        </Box>

        {/* Meta Information */}
        <Box sx={{ mb: 4, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar sx={{ bgcolor: '#FD8112' }}>
            {blog.author.split(' ').map(n => n[0]).join('')}
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: '600' }}>
              {blog.author}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {blog.date}
            </Typography>
          </Box>
          <Chip
            label={blog.category}
            sx={{
              ml: 'auto',
              backgroundColor: '#FD8112',
              color: 'white',
            }}
          />
        </Box>

        {/* Content */}
        <Box sx={{ mb: 6 }}>
          <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
            {blog.content}
          </ReactMarkdown>
        </Box>

        {/* Tags */}
        <Box sx={{ mb: 4 }}>
          {blog.tags.map((tag) => (
            <Chip
              key={tag}
              label={tag}
              sx={{
                mr: 1,
                mb: 1,
                backgroundColor: 'white',
                '&:hover': {
                  backgroundColor: '#f5f5f5',
                },
              }}
            />
          ))}
        </Box>

        {/* Share */}
        <Box sx={{ mb: 6, display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
          <Typography variant="body2" sx={{ fontWeight: 600, color: '#666', mr: 0.5 }}>
            Share:
          </Typography>
          {[
            {
              label: 'WhatsApp',
              icon: <WhatsAppIcon fontSize="small" />,
              href: `https://wa.me/?text=${shareText}%20${shareUrl}`,
              hoverColor: '#25D366',
            },
            {
              label: 'Facebook',
              icon: <FacebookIcon fontSize="small" />,
              href: `https://www.facebook.com/sharer/sharer.php?u=${shareUrl}`,
              hoverColor: '#1877F2',
            },
            {
              label: 'LinkedIn',
              icon: <LinkedInIcon fontSize="small" />,
              href: `https://www.linkedin.com/sharing/share-offsite/?url=${shareUrl}`,
              hoverColor: '#0A66C2',
            },
            {
              label: 'X',
              icon: <TwitterIcon fontSize="small" />,
              href: `https://twitter.com/intent/tweet?text=${shareText}&url=${shareUrl}`,
              hoverColor: '#000000',
            },
          ].map((s) => (
            <Box
              key={s.label}
              component="a"
              href={s.href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={`Share on ${s.label}`}
              sx={{
                width: 38,
                height: 38,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                borderRadius: '50%',
                color: '#666',
                backgroundColor: 'white',
                border: '1px solid #e0e0e0',
                transition: 'all 0.25s ease',
                '&:hover': { color: '#fff', backgroundColor: s.hoverColor, borderColor: s.hoverColor },
              }}
            >
              {s.icon}
            </Box>
          ))}
          <Box
            component="button"
            onClick={handleCopyLink}
            aria-label="Copy link to this article"
            sx={{
              width: 38,
              height: 38,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              borderRadius: '50%',
              color: '#666',
              backgroundColor: 'white',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'all 0.25s ease',
              '&:hover': { color: '#fff', backgroundColor: '#FD8112', borderColor: '#FD8112' },
            }}
          >
            <LinkIcon fontSize="small" />
          </Box>
        </Box>

        {/* FAQs */}
        {blog.faqs && blog.faqs.length > 0 && (
          <Box sx={{ mb: 6 }}>
            <Divider sx={{ mb: 4 }} />
            <Typography
              variant="h5"
              component="h2"
              sx={{ fontFamily: 'Nunito, serif', fontWeight: '600', mb: 3 }}
            >
              Frequently Asked Questions
            </Typography>
            {blog.faqs.map((faq, index) => (
              <Accordion key={index} sx={{ mb: 1, boxShadow: 'none', border: '1px solid #eee', '&:before': { display: 'none' } }}>
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Typography sx={{ fontWeight: 600, fontFamily: 'Nunito, serif' }}>{faq.question}</Typography>
                </AccordionSummary>
                <AccordionDetails>
                  <Typography sx={{ color: 'text.secondary', fontFamily: 'Nunito, serif', lineHeight: 1.7 }}>
                    {faq.answer}
                  </Typography>
                </AccordionDetails>
              </Accordion>
            ))}
          </Box>
        )}

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <Box>
            <Divider sx={{ mb: 4 }} />
            <Typography
              variant="h5"
              sx={{
                fontFamily: 'Nunito, serif',
                fontWeight: '600',
                mb: 3,
              }}
            >
              Related Articles
            </Typography>
            <Grid container spacing={3}>
              {relatedPosts.map((post) => (
                <Grid item xs={12} md={4} key={post.id}>
                  <Card
                    onClick={() => navigate(`/blog/${post.slug}`)}
                    sx={{
                      cursor: 'pointer',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        transform: 'translateY(-4px)',
                        boxShadow: 3,
                      },
                    }}
                  >
                    <Box
                      sx={{
                        height: 200,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={post.image}
                        alt={post.title}
                        sx={{
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                    </Box>
                    <CardContent>
                      <Typography
                        variant="h6"
                        sx={{
                          fontFamily: 'Nunito, serif',
                          fontWeight: '600',
                          mb: 1,
                        }}
                      >
                        {post.title}
                      </Typography>
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          mb: 2,
                          display: '-webkit-box',
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: 'vertical',
                          overflow: 'hidden',
                        }}
                      >
                        {post.shortDescription}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </Box>
        )}
      </Container>
    </Box>
  );
} 